import {
  BellIcon,
  BoltIcon,
  CheckIcon,
  ClockIcon,
  CreditCardIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  FunnelIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  Squares2X2Icon,
  UserIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { format, isToday, isTomorrow, isYesterday } from "date-fns";
import {
  activityOpens,
  formatAmount,
  invoicesRequestedTokenMap,
  planActionIdOf,
  taskItemFromActivity,
} from "@utils";
import { cn } from "@utils";
import { useCustomerActivities } from "@api/activitiesController";
import { useActionDetail } from "@api/actionsController";
import { useMessagesLookup, useMessagesByIds } from "@api/messagesController";
import { useAllUsers } from "@api/usersController";
import TaggedUsers from "@components/TaggedUsers";
import { ErpBadge, OntikBadge } from "@components/NoteSourceBadge";
import { MessageContentModal } from "@components";
import EventDetails from "@areas/calendar/displays/EventDetails";
import PlanDetailModal from "@areas/calendar/displays/PlanDetailModal";
import NoteDetailModal from "@components/NoteDetailModal";
import ActionStepTimeline from "./ActionStepTimeline";
import { CheckSquare } from "react-bootstrap-icons";
import SendClockIcon from "./SendClockIcon";
import {
  calendarLeadRow,
  groupSteps,
  visibleActionSteps,
  withScheduledSendTime,
} from "./actionSteps";

// The feed's timestamp standard: "Today at 1pm" / "Yesterday at 1pm", else the absolute
// "Fri, 3 Jul 2026 at 1pm". Time reads in plain words, dropping ":00" on the hour.
function formatActivityTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const time = format(date, date.getMinutes() === 0 ? "haaa" : "h:mmaaa");
  const day = isToday(date)
    ? "Today"
    : isYesterday(date)
      ? "Yesterday"
      : format(date, "EEE, d MMM yyyy");
  return `${day} at ${time}`;
}

// One entry per activity type: its icon, its icon colour, and the bold heading `label`
// shown ahead of the subtitle. Icon and label live together so a type can't have one
// without the other. Events with no `label` (the actions below) use their title as the
// heading, because the title is already the user-facing sentence.
const EVENT_CONFIG = {
  PaymentReceived: {
    Icon: CheckIcon,
    className: "bg-green-100 text-green-700",
    label: "Payment received",
  },
  // A chat-created task, sourced from the calendar's item-created event so the row carries the real
  // task title (the displayText) as its subtitle under the "Task created" heading.
  TaskCreated: {
    Icon: CheckSquare,
    className: "bg-slate-100 text-slate-700",
    label: "Task created",
  },
  PaymentRequestPaid: {
    Icon: CheckIcon,
    className: "bg-green-100 text-green-700",
    label: "Payment request paid",
  },
  PaymentRequestSent: {
    Icon: PaperAirplaneIcon,
    className: "bg-primary-50 text-primary-600",
    label: "Payment request sent",
  },
  PaymentRequestReminderSent: {
    Icon: BellIcon,
    className: "bg-amber-100 text-amber-700",
    label: "Reminder sent",
  },
  PaymentRequestCancelled: {
    Icon: XMarkIcon,
    className: "bg-zinc-100 text-zinc-500",
    label: "Payment request cancelled",
  },
  NoteCreated: {
    Icon: PencilSquareIcon,
    className: "bg-zinc-100 text-zinc-600",
    label: "Note",
  },
  CustomerCreated: {
    Icon: UserIcon,
    className: "bg-zinc-100 text-zinc-600",
    label: "Customer created",
  },
  CustomerUpdated: {
    Icon: UserIcon,
    className: "bg-zinc-100 text-zinc-600",
    label: "Customer updated",
  },
  CustomerDeleted: {
    Icon: UserIcon,
    className: "bg-zinc-100 text-zinc-600",
    label: "Customer deleted",
  },
  CreditLimitChanged: {
    Icon: CreditCardIcon,
    className: "bg-amber-100 text-amber-700",
    label: "Credit limit changed",
  },
  CreditCodeChanged: {
    Icon: CreditCardIcon,
    className: "bg-amber-100 text-amber-700",
    label: "Credit code changed",
  },
  InvoiceInQuery: {
    Icon: ExclamationTriangleIcon,
    className: "bg-amber-100 text-amber-700",
    label: "Invoice in query",
  },
  LedgerTransactionAdded: {
    Icon: DocumentTextIcon,
    className: "bg-zinc-100 text-zinc-600",
    label: "Ledger transaction",
  },
  DocumentSent: {
    Icon: DocumentTextIcon,
    className: "bg-sky-100 text-sky-700",
    label: "Documents sent",
  },
  // A plain email from the chat (no documents attached) — the displayText is its subject.
  EmailSent: {
    Icon: EnvelopeIcon,
    className: "bg-sky-100 text-sky-700",
    label: "Email sent",
  },
  // No label — the displayText is already the user-facing sentence ("Payment plan"),
  // used as the heading. Payment plan and promise to pay share this eventName, so their
  // icons/colours are resolved from the title (see ACTION_VISUALS); BoltIcon is the fallback.
  ActionStarted: {
    Icon: BoltIcon,
    className: "bg-violet-100 text-violet-700",
  },
  // A cancelled promise to pay / payment plan ("Payment plan cancelled") — muted like
  // PaymentRequestCancelled; the title carries the sentence, the body the detail.
  ActionCancelled: {
    Icon: XMarkIcon,
    className: "bg-zinc-100 text-zinc-500",
  },
};

// Icon/colour per plan action type — the same promise/indigo identity the calendar uses. Shared by
// ActionStarted rows (matched on title) and plan-originated payment-request sends (matched on the
// server-resolved originatingActionType).
const ACTION_TYPE_VISUALS = {
  PaymentPlan: { Icon: SendClockIcon, className: "bg-indigo-100 text-indigo-700" },
  PromiseToPay: { Icon: SendClockIcon, className: "bg-promise-100 text-promise-700" },
  // A merchant-scheduled payment request shares PromiseToPay's action type, so it's told apart by
  // the flat scheduleKind field (see resolveVisuals) rather than a key here. It reads as the send
  // it is — the plan's send-clock glyph in the payment-request orange.
  ScheduledPaymentRequest: { Icon: SendClockIcon, className: "bg-primary-50 text-primary-600" },
};

// Distinct icon/colour per action kind, matched on the (title-cased) displayText.
const ACTION_VISUALS = [
  { match: /^payment plan/i, ...ACTION_TYPE_VISUALS.PaymentPlan },
  { match: /^promise to pay/i, ...ACTION_TYPE_VISUALS.PromiseToPay },
  // A merchant-scheduled send's optimistic row is titled "Scheduled payment request …"; match it by
  // title too so it reads as a scheduled PR even before the canonical row (with scheduleKind) lands.
  { match: /^scheduled payment request/i, ...ACTION_TYPE_VISUALS.ScheduledPaymentRequest },
  // A chat-created task ("Task created") — the checkbox glyph the calendar and tasks page use, in
  // the neutral slate identity so it reads as its own thing rather than a muted system row.
  { match: /^task\b/i, Icon: CheckSquare, className: "bg-slate-100 text-slate-700" },
];

// Rows whose primary entity is the id of an email messaging-service sent (document send or plain email).
const EMAIL_MESSAGE_EVENTS = ["DocumentSent", "EmailSent"];

const DEFAULT_ICON = {
  Icon: ClockIcon,
  className: "bg-zinc-100 text-zinc-500",
};

// A task's scheduled due date, relative to now: "Today at 9:00am", "Tomorrow at 9:00am",
// else "Mon 15 Sep at 9:00am". Formatted client-side so it lands in the viewer's timezone.
function formatSchedule(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const time = `${format(d, "h:mm")}${format(d, "a").toLowerCase()}`;
  if (isToday(d)) return `Today at ${time}`;
  if (isTomorrow(d)) return `Tomorrow at ${time}`;
  return `${format(d, "EEE d MMM")} at ${time}`;
}

// activity.scheduleKind distinguishes a merchant-scheduled send from a customer promise
// to pay, both of which share the PromiseToPay action type/title shape. Falls back to the title
// regex above when the field is absent (older rows, or a rollout where the BFF hasn't deployed it
// yet), so nothing regresses.
function resolveVisuals(activity) {
  const scheduleKind = activity.scheduleKind;
  if (activity.eventName === "ActionStarted") {
    if (scheduleKind === "schedule") return ACTION_TYPE_VISUALS.ScheduledPaymentRequest;
    if (scheduleKind === "promise") return ACTION_TYPE_VISUALS.PromiseToPay;
    if (activity.title) {
      const hit = ACTION_VISUALS.find((v) => v.match.test(activity.title));
      if (hit) return hit;
    }
  }
  // A payment request sent as part of a plan / promise / schedule carries the plan glyph with a
  // small send badge, so the row reads as scheduled work rather than a one-off request.
  if (activity.eventName === "PaymentRequestSent") {
    if (scheduleKind === "schedule") {
      return { ...ACTION_TYPE_VISUALS.ScheduledPaymentRequest, badge: PaperAirplaneIcon };
    }
    if (ACTION_TYPE_VISUALS[activity.originatingActionType]) {
      return { ...ACTION_TYPE_VISUALS[activity.originatingActionType], badge: PaperAirplaneIcon };
    }
  }
  return EVENT_CONFIG[activity.eventName] ?? DEFAULT_ICON;
}

const PR_MESSAGE_EVENTS = ["PaymentRequestSent", "PaymentRequestReminderSent"];

// Ledger-transaction rows ("Invoice … remaining amount changed …") arrive in bursts when a batch
// of invoices settles, flooding the feed. A run of 2+ consecutive ones collapses into a single
// expandable summary; a lone one stays a normal row.
const LEDGER_EVENT = "LedgerTransactionUpdated";

function groupLedgerRuns(items) {
  const grouped = [];
  for (const activity of items) {
    const last = grouped[grouped.length - 1];
    if (activity.eventName === LEDGER_EVENT && last?.type === "ledger") {
      last.activities.push(activity);
    } else if (activity.eventName === LEDGER_EVENT) {
      grouped.push({ type: "ledger", key: activity.id, activities: [activity] });
    } else {
      grouped.push({ type: "single", key: activity.id, activity });
    }
  }
  // A run of one isn't worth collapsing — unwrap it back to a normal row.
  return grouped.map((entry) =>
    entry.type === "ledger" && entry.activities.length === 1
      ? { type: "single", key: entry.activities[0].id, activity: entry.activities[0] }
      : entry,
  );
}

// Activities carry a short displayText (`summary`) and, for some events, a full bodyText
// (`full`) of which the summary is a server-clipped version (e.g. notes are cut at ~140
// chars). Collapsed rows show the summary clamped to a few lines; "See more" swaps in the
// full text. Rows without a body still get "See more" when the summary itself visually
// overflows the clamp (measured, so short text never grows a link).
function ExpandableText({ summary, full, className, lines = 3 }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [clipped, setClipped] = useState(false);

  useLayoutEffect(() => {
    if (!expanded && ref.current) {
      setClipped(ref.current.scrollHeight > ref.current.clientHeight + 1);
    }
  }, [summary, expanded, lines]);

  const hasFullText = !!full && full !== summary;
  const text = expanded ? (full ?? summary) : summary;

  return (
    <>
      {text && (
        <div
          ref={ref}
          // The full bodyText keeps its original line breaks (the summary is one-lined
          // server-side), so respect them when expanded; collapsed stays clamped as a block.
          className={cn(className, expanded && "whitespace-pre-line")}
          style={
            expanded
              ? undefined
              : {
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: lines,
                  overflow: "hidden",
                }
          }
        >
          {text}
        </div>
      )}
      {(expanded || clipped || hasFullText) && (
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          className="text-xs font-medium text-zinc-400 hover:text-zinc-600"
        >
          {expanded ? "See less" : "See more"}
        </button>
      )}
    </>
  );
}

// Origin badge for note rows only — other feed events aren't user-authored records.
// The activities service serialises the ERP actor as snake_case "erp_user"
// (ActorTypeJsonConverter); "ErpUser" is accepted too in case that ever changes.
function resolveSourceBadge(activity) {
  if (activity.eventName !== "NoteCreated") return null;
  if (activity.actorType === "erp_user" || activity.actorType === "ErpUser")
    return <ErpBadge />;
  // "UserViaSuggestion" is a user variant (note added via an Ontik AI suggestion) — still Ontik.
  if (activity.actorType === "User" || activity.actorType === "UserViaSuggestion")
    return <OntikBadge />;
  return null;
}

function ActivityRow({ activity, actorName, taggedUsers, onViewMessage, onPreviewStep, onOpen }) {
  const { Icon, className, badge: BadgeIcon } = resolveVisuals(activity);
  // Plan/promise, task and note rows open the same detail modals the Today's-tasks AI Activity
  // panel opens, with the same whole-row hover. The row's own controls (See more, View message,
  // Show steps, step previews) are real buttons inside it; a click that lands on one of those is
  // theirs, not the row's.
  const opens = onOpen ? activityOpens(activity) : null;
  const onRowClick = (e) => {
    if (e.target.closest("button, a")) return;
    onOpen(activity);
  };
  const onRowKeyDown = (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen(activity);
    }
  };
  const label = EVENT_CONFIG[activity.eventName]?.label;
  // "promise" | "schedule" | undefined — tells a merchant-scheduled send apart from a customer
  // promise to pay, both of which arrive with the same PromiseToPay action type/title shape.
  const scheduleKind = activity.scheduleKind;
  const isScheduleKind = scheduleKind === "schedule";

  // Actions expand into their full step timeline (identical to the suggestion card); the steps
  // aren't in the feed, so they're fetched lazily on first expand.
  const isAction = activity.eventName === "ActionStarted";
  const isCancelled = activity.eventName === "ActionCancelled";
  // A chat-created task ("Task created") is a single calendar.task.create step — internal plumbing
  // with nothing useful to show — so its row keeps its full title and never expands.
  const isTask = isAction && /^task\b/i.test(activity.title ?? "");
  // Actions and cancellations have no fixed label — their title is the heading and their body
  // (the AI summary / "No longer active: …" reframing) is the subtitle. Everything else uses
  // its label as the heading and its displayText as the subtitle.
  const usesBodyAsSubtitle = isAction || isCancelled;

  // Note rows: the displayText is a server-clipped copy of the body (the full note content),
  // so "See more" on the subtitle swaps the full body in instead.
  const isNote = activity.eventName === "NoteCreated";

  // The action's displayText arrives as "<kind> started"; the feed shows just the kind. A task keeps
  // its full "Task created" title.
  const title =
    isAction && !isTask
      ? activity.title?.replace(/ started$/i, "").replace(/ created$/i, "")
      : activity.title;

  // Heading text: the fixed label where the event has one, else the title — overridden for a
  // merchant-scheduled send STARTING (not cancelled — that heading comes from the server's own
  // title, already "Scheduled payment request cancelled" via cancellationCopy) so it never reads
  // as a promise.
  const headingText = label ?? (isAction && isScheduleKind ? "Scheduled payment request" : title);

  // Promise-to-pay / scheduled-payment-request rows share the same action shape (and the title
  // regex is the back-compat fallback when schedule_kind is absent).
  const isPromiseToPay =
    /^promise to pay/i.test(activity.title ?? "") ||
    scheduleKind === "promise" ||
    scheduleKind === "schedule";
  // Promise-to-pay summaries read "… • Starts 20 Jul"; drop the verb, leave the date.
  const body =
    isAction && isPromiseToPay
      ? activity.body?.replace(/Starts /g, "")
      : activity.body;

  const canExpand = isAction && !isTask && !!activity.primaryEntityId;
  const [expanded, setExpanded] = useState(false);
  // Promise rows fetch their action eagerly (not just on expand) so the body can carry the send
  // time; other action rows still fetch only when expanded for the step timeline.
  const {
    data: action,
    isLoading,
    isError,
  } = useActionDetail(activity.primaryEntityId, canExpand && (expanded || isPromiseToPay));
  // Same as the suggestion timeline: for scheduled work, hide the internal plumbing steps
  // (paid-status checks, calendar entries, missed-payment follow-ups), leaving just the sends. A
  // schedule shares the promise's action/step shape, so it resolves the same "PromiseToPay" type
  // here — only the heading/icon/colour (above) and the calendar-lead copy (via kind) differ.
  const actionType = isPromiseToPay
    ? "PromiseToPay"
    : /^payment plan/i.test(activity.title ?? "")
      ? "PaymentPlan"
      : null;
  let groups = action?.steps
    ? groupSteps(visibleActionSteps(action.steps, actionType))
    : [];
  const lead = calendarLeadRow(actionType, groups, isScheduleKind ? "schedule" : undefined);
  if (lead) groups = [lead, ...groups];

  // A promise-to-pay body ("£300 · Fri, 21 Aug 2026") ends with the promised date; show the
  // scheduled send time — read from the action's send step, the same source the suggestion card
  // uses — so it reads "… at 7am". Idempotent (won't double a time the model already wrote); the
  // time appears once the action detail loads.
  const effectiveBody =
    isPromiseToPay && action?.steps
      ? withScheduledSendTime(body, groupSteps(visibleActionSteps(action.steps, "PromiseToPay")))
      : body;

  // Payment requests read as a two-line row matching the suggestion step: a plain "Payment
  // request sent" heading, with the request's shape on the subtitle — "Custom amount · £80.00"
  // for a fixed amount, or the invoice bucket ("Overdue invoices") for a statement send. Same
  // for AI and manual sends. Every other event keeps the amount on the heading.
  const isPaymentRequest = activity.eventName === "PaymentRequestSent";
  const paymentRequestSubtitle = () => {
    const bucket =
      invoicesRequestedTokenMap[activity.invoicesRequested] ?? "Custom amount";
    return activity.amount != null
      ? `${bucket} · ${formatAmount(activity.amount)}`
      : bucket;
  };

  // Bold heading with the amount appended when the event carries one ("Payment received ·
  // £150.00"). Amounts always read on the title line, never the subtitle — except payment
  // requests, whose amount reads on the subtitle beside the request shape (see above).
  const heading =
    activity.amount != null && !isPaymentRequest
      ? `${headingText} · ${formatAmount(activity.amount)}`
      : headingText;

  // Subtitle under the heading: the request shape for payment requests, else the display
  // sentence (or the body, for actions/cancellations). A chat-created task appends its
  // scheduled due date, so the row reads "Call Paul · Tomorrow at 9:00am".
  const schedule =
    activity.eventName === "TaskCreated" ? formatSchedule(activity.scheduledFor) : null;
  const baseDetail = title && title !== label ? title : null;
  const detail =
    baseDetail && schedule ? `${baseDetail} · ${schedule}` : baseDetail;
  const subtitle = isPaymentRequest
    ? paymentRequestSubtitle()
    : usesBodyAsSubtitle
      ? (effectiveBody ?? "")
      : label && detail
        ? detail
        : "";
  // Notes clip their body into the displayText; "See more" swaps the full note back in.
  const subtitleFull = isNote ? activity.body : null;

  const row = (
    <div className="flex gap-3">
      {/* size-8 (matching the icon) keeps the wrapper from stretching to the row height,
          so the absolutely-positioned pill anchors to the icon's corner, not the row's. */}
      <span className="relative size-8 shrink-0">
        <span
          className={cn(
            "flex size-8 items-center justify-center rounded-lg",
            className,
          )}
        >
          <Icon aria-hidden="true" className="size-4" />
        </span>
        {/* A plan/promise-originated send keeps the plan glyph and adds a small payment-request
            badge in the corner (matching the plan modal's PromiseToPayCard). */}
        {BadgeIcon && (
          <span className="absolute -bottom-0.5 -right-0.5 flex size-3.5 items-center justify-center rounded-full bg-primary-50 text-primary-600 ring-2 ring-white">
            <BadgeIcon aria-hidden="true" className="size-2.5" />
          </span>
        )}
        {/* Source pill overlaps the icon's bottom-right corner (note rows only); the white
            ring separates it from the icon behind it. */}
        <span className="absolute -bottom-0.5 -right-0.5 flex rounded-[4px] ring-2 ring-white">
          {resolveSourceBadge(activity)}
        </span>
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-zinc-900">{heading}</div>
        <ExpandableText
          summary={subtitle}
          full={subtitleFull}
          className="mt-2 text-sm text-zinc-900"
        />
        <TaggedUsers users={taggedUsers ?? activity.taggedUsers} />
        <div className="mt-2 text-xs text-zinc-400">
          {formatActivityTime(activity.occurredAt)}
          {actorName && <> · {actorName}</>}
        </div>

        {onViewMessage && (
          <button
            type="button"
            onClick={onViewMessage}
            className="mt-1 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-zinc-600"
          >
            <EyeIcon aria-hidden="true" className="size-3.5" />
            View message
          </button>
        )}

        {canExpand && (
          <>
            <button
              type="button"
              onClick={() => setExpanded((e) => !e)}
              className="mt-1 flex items-center gap-1 text-xs font-medium text-zinc-400 hover:text-zinc-600"
            >
              {expanded ? "Hide steps" : "Show steps"}
              <ChevronDownIcon
                aria-hidden="true"
                className={cn(
                  "size-3.5 transition-transform duration-200",
                  expanded && "rotate-180",
                )}
              />
            </button>
            {expanded && (
              <div className="mt-1">
                {isLoading && (
                  <div className="text-xs text-zinc-400">Loading steps…</div>
                )}
                {isError && (
                  <div className="text-xs text-zinc-400">Couldn’t load steps.</div>
                )}
                <ActionStepTimeline
                  groups={groups}
                  expanded
                  instant
                  onPreviewStep={onPreviewStep}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Same affordance as the AI Activity panel's rows: the row lifts on hover and takes focus.
  return opens ? (
    <div
      role="button"
      tabIndex={0}
      onClick={onRowClick}
      onKeyDown={onRowKeyDown}
      className="-m-1 rounded-lg p-1 transition-colors hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
    >
      {row}
    </div>
  ) : (
    row
  );
}

// A collapsed run of consecutive ledger-transaction rows: one summary line that expands to the
// individual rows (rendered as normal ActivityRows). Collapsed by default so batches stay quiet.
function LedgerGroupRow({ activities, actorNameFor, onViewMessageFor }) {
  const [expanded, setExpanded] = useState(false);
  const count = activities.length;
  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center gap-3 text-left"
      >
        <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600">
          <DocumentTextIcon aria-hidden="true" className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-zinc-900">
            {count} ledger transactions
          </div>
          <div className="mt-1.5 text-xs text-zinc-400">
            {formatActivityTime(activities[0].occurredAt)}
          </div>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-zinc-400">
          {expanded ? "Hide" : "Show"}
          <ChevronDownIcon
            aria-hidden="true"
            className={cn("size-4 transition-transform duration-200", expanded && "rotate-180")}
          />
        </span>
      </button>
      {expanded && (
        <div className="space-y-4 border-l border-zinc-100 pl-4">
          {activities.map((activity) => (
            <ActivityRow
              key={activity.id}
              activity={activity}
              actorName={actorNameFor(activity)}
              onViewMessage={onViewMessageFor(activity)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ACTIVITY_FILTERS = [
  { key: "all", label: "All" },
  { key: "payments", label: "Payments" },
  { key: "notes", label: "Notes" },
  { key: "comms", label: "Comms" },
  { key: "tasks", label: "Tasks" },
];

// The type dropdown's options, partitioned like the filter chips — mirrors activities-service's
// CustomerTimelineEventNames (minus the legacy ActionCreated/ActionUpdated rows).
const TYPE_SECTIONS = [
  {
    key: "payments",
    label: "Payments",
    types: [
      { key: "PaymentReceived", label: "Payment received" },
      { key: "PaymentRequestSent", label: "Payment request sent" },
      { key: "PaymentRequestReminderSent", label: "Reminder sent" },
      { key: "PaymentRequestPaid", label: "Payment request paid" },
      { key: "PaymentRequestCancelled", label: "Payment request cancelled" },
      { key: "ActionStarted", label: "Plan or promise started" },
      { key: "ActionCancelled", label: "Plan or promise cancelled" },
    ],
  },
  { key: "notes", label: "Notes", types: [{ key: "NoteCreated", label: "Note" }] },
  {
    key: "comms",
    label: "Comms",
    types: [
      { key: "EmailSent", label: "Email sent" },
      { key: "DocumentSent", label: "Documents sent" },
    ],
  },
  { key: "tasks", label: "Tasks", types: [{ key: "TaskCreated", label: "Task created" }] },
  {
    key: "other",
    label: "Other",
    types: [
      { key: LEDGER_EVENT, label: "Ledger transaction" },
      { key: "InvoiceSettled", label: "Invoice settled" },
      { key: "CreditLimitChanged", label: "Credit limit changed" },
      { key: "CreditCodeChanged", label: "Credit code changed" },
      { key: "CustomerCreated", label: "Customer created" },
      { key: "CustomerUpdated", label: "Customer updated" },
      { key: "CustomerDeleted", label: "Customer deleted" },
    ],
  },
];

const inCategory = (eventName, category) =>
  category === "all" ||
  !!TYPE_SECTIONS.find((section) => section.key === category)?.types.some((type) => type.key === eventName);

export default function ActivityFeed({
  customerId,
  scrollContainerRef,
  enterId,
  enterDelay = 0,
  spacious = false,
  onPreviewStep,
  onOpenPaymentRequest,
}) {
  // Active filter chip (spacious variant only). Default "all" keeps the compact V1 feed identical.
  const [filter, setFilter] = useState("all");
  const [selectedTypes, setSelectedTypes] = useState([]);

  // Filtered server-side: a client-side filter over paged results would page through the whole
  // history hunting for matches when the chosen types are rare.
  const eventNames = useMemo(() => {
    if (selectedTypes.length > 0) return [...selectedTypes].sort();
    const section = TYPE_SECTIONS.find((s) => s.key === filter);
    return section ? section.types.map((type) => type.key).sort() : null;
  }, [filter, selectedTypes]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCustomerActivities(customerId, { eventNames });

  // Some events (e.g. document sends) carry only the acting user's guid; the merchant's
  // user list resolves it to a display name. An event-carried label always wins.
  // useAllUsers, not useUsers: support users must stay resolvable here, or their
  // notes render with no author.
  const { data: users = [] } = useAllUsers();
  const userNamesById = useMemo(
    () => new Map(users.map((user) => [String(user.id).toLowerCase(), user.name])),
    [users],
  );
  const actorNameFor = (activity) =>
    activity.actorLabel ??
    (activity.actorId ? userNamesById.get(activity.actorId.toLowerCase()) : null);

  // Assigned colleagues on a task row arrive as ids with no name (unlike a note, whose event carries
  // names), so resolve them against the merchant's user list — the same source the actor name uses.
  const taggedUsersFor = (activity) =>
    (activity.taggedUsers ?? []).map((user) =>
      user.name?.trim() || !user.userId
        ? user
        : { ...user, name: userNamesById.get(String(user.userId).toLowerCase()) ?? user.name },
    );

  const items = useMemo(
    () => data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    [data],
  );


  // A freshly-accepted row must be visible for its entrance, so reset to All when one arrives.
  // enterId is only ever set by the V2 account view, so this is a no-op in the flag-off flow.
  useEffect(() => {
    if (enterId) {
      setFilter("all");
      setSelectedTypes([]);
    }
  }, [enterId]);

  const selectCategory = (category) => {
    setFilter(category);
    setSelectedTypes((types) => types.filter((type) => inCategory(type, category)));
  };

  const toggleType = (eventName) =>
    setSelectedTypes((types) =>
      types.includes(eventName) ? types.filter((t) => t !== eventName) : [...types, eventName],
    );

  const typeSections = TYPE_SECTIONS.filter(
    (section) => filter === "all" || filter === section.key,
  );

  // Feed rows with consecutive ledger-transaction runs folded into collapsible groups.
  const groupedItems = useMemo(() => groupLedgerRuns(items), [items]);

  const paymentRequestIds = useMemo(
    () => [...new Set(items
      .filter((a) => PR_MESSAGE_EVENTS.includes(a.eventName) && a.primaryEntityId)
      .map((a) => a.primaryEntityId))],
    [items],
  );

  // Both rows carry the sent email's message id as their primary entity, so both can open the
  // archived message.
  const documentMessageIds = useMemo(
    () => [...new Set(items
      .filter((a) => EMAIL_MESSAGE_EVENTS.includes(a.eventName) && a.primaryEntityId)
      .map((a) => a.primaryEntityId))],
    [items],
  );

  const { data: requestMessages } = useMessagesLookup(paymentRequestIds);
  const { data: documentMessages } = useMessagesByIds(documentMessageIds);

  const archivedRequestIds = useMemo(
    () => new Set((requestMessages ?? []).filter((m) => m.hasArchive).map((m) => m.paymentRequestId)),
    [requestMessages],
  );

  const archivedMessageIds = useMemo(
    () => new Set((documentMessages ?? []).filter((m) => m.hasArchive).map((m) => m.id)),
    [documentMessages],
  );

  const [viewMessage, setViewMessage] = useState(null);

  // The detail opened from a row — { kind: "plan" | "task" | "note", activity } — rendered by the
  // same modals the Today's-tasks AI Activity panel uses, elevated above this (already modal) feed.
  const [detail, setDetail] = useState(null);
  const onOpen = (activity) => {
    const kind = activityOpens(activity);
    if (!kind) return;
    // The note modal names the author; the row resolves it from the merchant's user list.
    setDetail({ kind, activity: { ...activity, actorName: actorNameFor(activity) } });
  };

  const onViewMessageFor = (activity) => {
    const entityId = activity.primaryEntityId;
    if (!entityId) return null;
    if (PR_MESSAGE_EVENTS.includes(activity.eventName) && archivedRequestIds.has(entityId)) {
      return () => setViewMessage({ paymentRequestId: entityId, date: activity.occurredAt });
    }
    if (EMAIL_MESSAGE_EVENTS.includes(activity.eventName) && archivedMessageIds.has(entityId)) {
      return () => setViewMessage({ messageId: entityId });
    }
    return null;
  };

  // Infinite scroll: a sentinel at the feed's tail pulls the next page as it nears the
  // panel's scroll viewport (rootMargin pre-fetches just before it's reached). Falls back to
  // the browser viewport if no scroll container was passed.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: scrollContainerRef?.current ?? null, rootMargin: "160px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, scrollContainerRef]);

  // `spacious` is the roomy variant used by the V2 account view: no card chrome or header (the tab
  // supplies both), and each row sits in its own padded, roundable band so the feed breathes. The
  // default (false) is the compact V1 card — kept byte-for-byte so the flag-off flow is untouched.
  const singleRow = (entry) => (
    <ActivityRow
      activity={entry.activity}
      actorName={actorNameFor(entry.activity)}
      taggedUsers={taggedUsersFor(entry.activity)}
      onViewMessage={onViewMessageFor(entry.activity)}
      onPreviewStep={onPreviewStep}
      onOpen={onOpen}
    />
  );

  return (
    <div className={spacious ? "" : "rounded-2xl border border-zinc-200 bg-white p-4"}>
      {!spacious && (
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
            <Squares2X2Icon aria-hidden="true" className="size-4" />
            Recent activity
          </span>
        </div>
      )}

      {spacious && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {ACTIVITY_FILTERS.map((f) => {
              const selected = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => selectCategory(f.key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                    selected
                      ? "border-zinc-300 bg-zinc-100 text-zinc-900"
                      : "border-zinc-200 bg-white text-zinc-600 hover:bg-zinc-50",
                  )}
                >
                  {f.label}
                </button>
              );
            })}
          </div>

          <Popover className="relative ml-auto">
            <PopoverButton
              aria-label="Filter by activity type"
              title="Filter by activity type"
              className={cn(
                "inline-flex items-center gap-1 rounded-full border p-2 text-sm font-medium transition-colors focus:outline-none",
                selectedTypes.length === 0
                  ? "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50 hover:text-zinc-700"
                  : "border-zinc-300 bg-zinc-100 text-zinc-900",
              )}
            >
              <FunnelIcon aria-hidden="true" className="size-4" />
              {selectedTypes.length > 0 && (
                <span className="rounded-full bg-primary-100 px-1.5 text-xs font-semibold text-primary-700">
                  {selectedTypes.length}
                </span>
              )}
            </PopoverButton>
            <PopoverPanel className="absolute right-0 z-30 mt-2 max-h-96 w-64 origin-top-right overflow-y-auto rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-sm font-medium text-zinc-900">Activity types</span>
                {selectedTypes.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedTypes([])}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    Clear
                  </button>
                )}
              </div>
              {typeSections.map((section) => (
                <div key={section.key} className="border-t border-zinc-100 pt-1">
                  <div className="px-2 pb-0.5 pt-1.5 text-xs font-medium uppercase tracking-wide text-zinc-400">
                    {section.label}
                  </div>
                  {section.types.map((type) => (
                    <label
                      key={type.key}
                      className="flex cursor-pointer items-center gap-2.5 rounded px-2 py-1.5 hover:bg-zinc-50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedTypes.includes(type.key)}
                        onChange={() => toggleType(type.key)}
                        className="size-4 shrink-0 rounded border-zinc-300 accent-primary-500"
                      />
                      <span className="truncate text-sm text-zinc-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              ))}
            </PopoverPanel>
          </Popover>
        </div>
      )}

      <div className={spacious ? "space-y-1" : "mt-4 space-y-6"}>
        {isLoading && (
          <div className="text-sm text-zinc-400">Loading activity…</div>
        )}

        {isError && (
          <div className="text-sm text-zinc-400">Couldn’t load activity.</div>
        )}

        {!isLoading && !isError && items.length === 0 && (
          eventNames ? (
            <div className="px-1 py-6 text-sm text-zinc-400">
              {selectedTypes.length === 0
                ? `No ${ACTIVITY_FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} activity yet.`
                : "No activity of the selected types yet."}
            </div>
          ) : (
            <div className="text-sm text-zinc-400">No recent activity.</div>
          )
        )}

        {groupedItems.map((entry) =>
          entry.type === "single" ? (
            entry.key === enterId ? (
              // The just-accepted row eases open from the top (grid 0fr→1fr), wiping the rest of the
              // feed down; the content settles into place. In the spacious variant a soft orange wash
              // fills its padded band and fades out — the padding gives the pulse room to look good.
              <div
                key={entry.key}
                className="smart-row-in grid"
                style={{ "--enter-delay": `${enterDelay}ms` }}
              >
                <div className="overflow-hidden">
                  <div className="smart-row-settle">
                    {spacious ? (
                      <div className="smart-row-pulse rounded-2xl px-4 py-4">{singleRow(entry)}</div>
                    ) : (
                      singleRow(entry)
                    )}
                  </div>
                </div>
              </div>
            ) : spacious ? (
              <div
                key={entry.key}
                className="rounded-2xl px-4 py-4 transition-colors hover:bg-zinc-50"
              >
                {singleRow(entry)}
              </div>
            ) : (
              <ActivityRow
                key={entry.key}
                activity={entry.activity}
                actorName={actorNameFor(entry.activity)}
                taggedUsers={taggedUsersFor(entry.activity)}
                onViewMessage={onViewMessageFor(entry.activity)}
                onPreviewStep={onPreviewStep}
                onOpen={onOpen}
              />
            )
          ) : spacious ? (
            <div key={entry.key} className="rounded-2xl px-4 py-4 transition-colors hover:bg-zinc-50">
              <LedgerGroupRow
                activities={entry.activities}
                actorNameFor={actorNameFor}
                onViewMessageFor={onViewMessageFor}
              />
            </div>
          ) : (
            <LedgerGroupRow
              key={entry.key}
              activities={entry.activities}
              actorNameFor={actorNameFor}
              onViewMessageFor={onViewMessageFor}
            />
          ),
        )}

        {/* Tail sentinel + loader — one fixed-height band while more pages exist, so toggling the loader
            text never shifts layout. overflow-anchor:none keeps the browser from anchoring scroll to this
            (it's removed on the last page) — it anchors to a real row instead, so the viewport holds its
            place as earlier pages' async content (message lookups, actor names) settles. */}
        {hasNextPage && (
          <div
            ref={sentinelRef}
            style={{ overflowAnchor: "none" }}
            className="flex h-9 items-center justify-center text-sm text-zinc-400"
          >
            {isFetchingNextPage && "Loading…"}
          </div>
        )}
      </div>

      <MessageContentModal
        isOpen={!!viewMessage}
        onClose={() => setViewMessage(null)}
        messageId={viewMessage?.messageId ?? null}
        paymentRequestId={viewMessage?.paymentRequestId ?? null}
        targetDate={viewMessage?.date}
        elevated
      />

      {detail?.kind === "plan" && (
        <PlanDetailModal
          actionId={planActionIdOf(detail.activity)}
          isOpen
          onClose={() => setDetail(null)}
          onOpenPaymentRequest={onOpenPaymentRequest}
          elevated
        />
      )}

      {detail?.kind === "task" && (
        <EventDetails
          item={taskItemFromActivity(detail.activity)}
          isOpen
          onClose={() => setDetail(null)}
          readOnly
          elevated
        />
      )}

      {detail?.kind === "note" && (
        <NoteDetailModal
          activity={detail.activity}
          isOpen
          onClose={() => setDetail(null)}
          elevated
        />
      )}
    </div>
  );
}
